import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,  Divider,
  LinearProgress,
  useTheme,
} from '@mui/material';
import { Job } from '../models/types';
import { useNavigate } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import PersonIcon from '@mui/icons-material/Person';

interface JobCardProps {
  job: Job & { coverage?: number; totalTimeslots?: number };
}

const JobCard: React.FC<JobCardProps> = ({ job }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleViewDetails = () => {
    navigate(`/jobs/${job.id}`);
  };
  const getCoverageColor = () => {
    if (!job.coverage) return 'error.main';
    if (job.coverage < 50) return 'error.main';
    if (job.coverage < 75) return 'warning.main';
    return 'success.main';
  };
  
  const getCoverageStatus = () => {
    if (!job.coverage) return 'Keine Daten';
    if (job.coverage < 50) return 'Dringend Helfer benötigt';
    if (job.coverage < 75) return 'Weitere Helfer benötigt';
    return 'Gut besetzt';
  };
  
  const coveragePercent = job.coverage ? Math.round(job.coverage) : 0;

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderLeft: 4,
        borderLeftColor: getCoverageColor()
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h5" component="h2">
          {job.name}
        </Typography>
  
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PlaceIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {job.location}
            </Typography>
          </Box>
          
          {job.responsible && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Verantwortlich: {job.responsible}
              </Typography>
            </Box>
          )}
          
          {job.totalTimeslots !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {job.totalTimeslots} {job.totalTimeslots === 1 ? 'Zeitfenster' : 'Zeitfenster'}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {job.requiredVolunteers} benötigte Helfer
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />
        
        <Typography variant="body2" paragraph sx={{ 
          display: '-webkit-box', 
          WebkitLineClamp: 3, 
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          mb: 2 
        }}>
          {job.description}
        </Typography>
        
        {job.coverage !== undefined && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(100, coveragePercent)} 
                color={
                  job.coverage < 50 ? 'error' :
                  job.coverage < 75 ? 'warning' : 
                  'success'
                }
                sx={{ 
                  height: 8, 
                  borderRadius: 4, 
                  width: '100%',
                  backgroundColor: theme.palette.grey[200] 
                }}
              />
              <Typography variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                {coveragePercent}%
              </Typography>
            </Box>
            <Typography variant="caption" color={getCoverageColor()} sx={{ fontWeight: 500 }}>
              {getCoverageStatus()}
            </Typography>
          </Box>
        )}
      </CardContent>
        <CardActions>
        <Button 
          size="small" 
          color="primary" 
          variant={"contained"}
          onClick={handleViewDetails}
          fullWidth
          disabled={coveragePercent >= 100}
        >
          {coveragePercent >= 100 ? 'Ausgebucht' : 'Dienste anzeigen'}
        </Button>
      </CardActions>
    </Card>
  );
};

export default JobCard;
